/**
 * Identifiers minted on the device, before anything has been near a network.
 *
 * An adapter rather than a bare `crypto.randomUUID()` call because the
 * capability genuinely differs by host, which is the ADR-0021 test: the Web
 * Crypto API is gated on a SECURE CONTEXT, so a coach opening the app over
 * plain http on a gym LAN has `crypto.randomUUID` undefined while a Capacitor
 * shell always has it. That is the one place the event id is generated, and an
 * exception there loses the play.
 */

import type { Adapter } from './host.js';

export interface Ids extends Adapter {
  /**
   * A lowercase v4 uuid.
   *
   * Lowercase is load-bearing rather than cosmetic. It is the casing the store
   * keeps, and an id that goes up in another casing comes back lowercased, so
   * every client-side comparison against a server echo fails: the `known` list
   * never matches what the outbox sent and the event is never retired.
   */
  readonly uuid: () => string;
}

interface CryptoLike {
  randomUUID?: () => string;
  getRandomValues?: <T extends Uint8Array>(array: T) => T;
}

function webCrypto(): CryptoLike | null {
  return typeof globalThis.crypto === 'undefined' ? null : globalThis.crypto;
}

/**
 * A v4 uuid from raw random bytes, for a host with `getRandomValues` and no
 * `randomUUID`. Not a weaker id: the same 122 bits from the same source, only
 * formatted here instead of by the browser.
 */
function fromBytes(bytes: Uint8Array): string {
  // Version 4 in the high nibble of byte 6, variant 10 in the top bits of
  // byte 8. Without both, this is 128 random bits wearing a uuid's punctuation
  // and `z.uuid()` refuses it at the server door.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

export const ids: Ids = {
  isSupported: () =>
    typeof webCrypto()?.randomUUID === 'function' ||
    typeof webCrypto()?.getRandomValues === 'function',

  /*
   * This is the one adapter method that cannot no-op when unsupported, and the
   * package contract's "never throw" still holds. A scorebook with no id has
   * no event, so the last resort is `Math.random`, which is the wrong tool for
   * a token and the right one here: the property an event id needs is
   * uniqueness within one device's game, not unpredictability. Nothing
   * authorises on it.
   */
  uuid: () => {
    const source = webCrypto();
    if (typeof source?.randomUUID === 'function') return source.randomUUID().toLowerCase();

    const bytes = new Uint8Array(16);
    if (typeof source?.getRandomValues === 'function') {
      source.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }
    return fromBytes(bytes);
  },
};
